import React, { useMemo } from 'react';
import { Typography, Tag, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import PageList from '../../components/Templates/PageList';
import type { FieldConfig } from '../../components/ForgeQueryBuilder';
import { operationLogApi } from '../../services/process/operation-log';
import { useStaffMap } from '../../services/store/organization.store';
import { OrderOperationLog, OperationModuleNameMap, OperationActionNameMap, OperationAction } from '../../types/process/operation-log';
import dayjs from 'dayjs';

const { Text } = Typography;

const OperationLogs: React.FC = () => {
  const { staffMap } = useStaffMap();

  const queryFields: FieldConfig[] = useMemo(() => [
    { 
      key: 'created_at', 
      label: '操作时间', 
      type: 'date', 
      operators: ['between'], 
      format: 'YYYY-MM-DD', 
      showTime: true,
      placeholder: ['开始时间', '结束时间'] 
    },
    { key: 'order_no', label: '订单号', type: 'string', operators: ['eq'], trim: true },
    { 
      key: 'operator_id', 
      label: '操作人', 
      type: 'select', 
      operators: ['eq'],
      placeholder: '选择操作人',
      options: Array.from(staffMap.values()).map(s => ({ label: s.name, value: s.id })),
      showSearch: true
    },
    { 
      key: 'module', 
      label: '操作模块', 
      type: 'select', 
      operators: ['eq'],
      options: Object.entries(OperationModuleNameMap).map(([value, label]) => ({ label, value })) 
    },
    { 
      key: 'action', 
      label: '操作内容', 
      type: 'multiSelect', 
      operators: ['in'],
      width: 300,
      options: Object.values(OperationAction).map(action => ({
        label: OperationActionNameMap[action] || action,
        value: action
      }))
    },
  ], [staffMap]);

  const defaultQuery = useMemo(() => ({
    filter: {
      created_at: {
        operator: 'between',
        value: [
          dayjs().subtract(6, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
          dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss')
        ]
      }
    }
  }), []);

  const columns: ColumnsType<OrderOperationLog> = [
    {
      title: '订单号',
      key: 'orderNo',
      width: 180,
      render: (_, rec) => <Text copyable>{rec.orderNo}</Text>,
    },
    {
      title: '操作人',
      key: 'operatorId',
      width: 120,
      render: (_, rec) => {
        const staff = staffMap.get(rec.operatorId);
        return <Text>{staff ? staff.name : rec.operatorId}</Text>;
      },
    },
    {
      title: '操作模块',
      key: 'module',
      width: 120,
      render: (_, rec) => <Tag color="blue">{OperationModuleNameMap[rec.module] || rec.module}</Tag>,
    },
    {
      title: '操作内容',
      key: 'action',
      width: 300,
      render: (_, rec) => <Text>{OperationActionNameMap[rec.action] || rec.action}</Text>,
    },
    {
      title: '操作时间',
      key: 'createdAt',
      width: 180,
      render: (_, rec) => <Text>{dayjs(rec.createdAt).format('YYYY-MM-DD HH:mm:ss')}</Text>,
    },
  ];

  return (
    <PageList<OrderOperationLog>
      title="操作日志管理"
      queryFields={queryFields}
      defaultQuery={defaultQuery}
      fetcher={async (params) => {
        const fetchParams = {
          ...params,
          sort: params.sort?.length ? params.sort : [{ field: 'id', order: 'desc' }]
        };
        const res = await operationLogApi.list(fetchParams);
        return {
          rows: res.data.items,
          pagination: res.data.pagination,
        };
      }}
      renderList={({ rows, loading }) => (
        <div style={{ background: '#fff' }}>
           <Table
             loading={loading}
             dataSource={rows}
             columns={columns}
             rowKey="id"
             pagination={false}
           />
        </div>
      )}
    />
  );
};

export default OperationLogs;
